import { useState, useRef, useCallback, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

type MediaCategory = 'all' | 'products' | 'banners' | 'hero' | 'other';
type ViewMode = 'grid' | 'list';
type SortMode = 'newest' | 'oldest' | 'name' | 'size';

interface MediaFile {
  id: string;
  name: string;
  url: string;
  size: number;
  created_at: string;
  bucket: string;
  path: string;
  category: MediaCategory;
  width?: number;
  height?: number;
}

interface UploadingFile {
  id: string;
  name: string;
  progress: number;
  stage: 'compressing' | 'uploading' | 'done' | 'error';
  preview: string;
  originalSize: number;
  compressedSize?: number;
  error?: string;
  category: MediaCategory;
}

const BUCKET_MAP: Record<Exclude<MediaCategory, 'all'>, string> = {
  products: 'product-images',
  banners: 'banners',
  hero: 'banners',
  other: 'product-images',
};

const CATEGORY_LABELS: Record<MediaCategory, string> = {
  all: 'الكل',
  products: 'صور المنتجات',
  banners: 'البانرات',
  hero: 'صور الهيرو',
  other: 'أخرى',
};

const CATEGORY_ICONS: Record<MediaCategory, string> = {
  all: 'ri-image-2-line',
  products: 'ri-shopping-bag-3-line',
  banners: 'ri-layout-top-line',
  hero: 'ri-image-fill',
  other: 'ri-folder-image-line',
};

const CATEGORY_COLORS: Record<MediaCategory, string> = {
  all: 'text-gray-600 bg-gray-100',
  products: 'text-emerald-700 bg-emerald-50',
  banners: 'text-violet-700 bg-violet-50',
  hero: 'text-amber-700 bg-amber-50',
  other: 'text-gray-600 bg-gray-100',
};

const MAX_DIMENSION = 1600;
const QUALITY = 0.85;
const MAX_RAW_SIZE = 15 * 1024 * 1024;

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('ar-SA', { year: 'numeric', month: 'short', day: 'numeric' });
}

async function compressImage(file: File, maxDim = MAX_DIMENSION): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width >= height) { height = Math.round((height / width) * maxDim); width = maxDim; }
        else { width = Math.round((width / height) * maxDim); height = maxDim; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('canvas unavailable')); return; }
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(blob => {
        if (!blob) { reject(new Error('compression failed')); return; }
        resolve(blob);
      }, 'image/webp', QUALITY);
    };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('load failed')); };
    img.src = objectUrl;
  });
}

interface MediaLibraryProps {
  onToast: (msg: string, type?: 'success' | 'error') => void;
}

export default function MediaLibrary({ onToast }: MediaLibraryProps) {
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState<UploadingFile[]>([]);
  const [activeCategory, setActiveCategory] = useState<MediaCategory>('all');
  const [uploadCategory, setUploadCategory] = useState<Exclude<MediaCategory, 'all'>>('products');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [sortMode, setSortMode] = useState<SortMode>('newest');
  const [search, setSearch] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [previewFile, setPreviewFile] = useState<MediaFile | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<MediaFile | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [copyTooltip, setCopyTooltip] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const buckets = ['product-images', 'banners'];
      const allFiles: MediaFile[] = [];

      for (const bucket of buckets) {
        const { data, error } = await supabase.storage.from(bucket).list('', {
          limit: 500,
          sortBy: { column: 'created_at', order: 'desc' },
        });
        if (error || !data) continue;

        // Also check subfolders
        const folders = ['products', 'banners', 'hero', ''];
        for (const folder of folders) {
          const prefix = folder ? `${folder}/` : '';
          const { data: folderData } = await supabase.storage.from(bucket).list(folder || undefined, {
            limit: 500,
            sortBy: { column: 'created_at', order: 'desc' },
          });
          if (!folderData) continue;

          for (const item of folderData) {
            if (item.name === '.emptyFolderPlaceholder') continue;
            if (!item.metadata) continue; // skip folders
            const path = prefix + item.name;
            const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(path);
            let category: MediaCategory = 'other';
            if (bucket === 'product-images') category = 'products';
            else if (folder === 'banners') category = 'banners';
            else if (folder === 'hero') category = 'hero';
            else if (bucket === 'banners') category = 'banners';

            allFiles.push({
              id: `${bucket}/${path}`,
              name: item.name,
              url: urlData.publicUrl,
              size: item.metadata?.size ?? 0,
              created_at: item.created_at ?? new Date().toISOString(),
              bucket,
              path,
              category,
            });
          }
        }
      }

      // Deduplicate by id
      const seen = new Set<string>();
      const unique = allFiles.filter(f => {
        if (seen.has(f.id)) return false;
        seen.add(f.id);
        return true;
      });

      setFiles(unique);
    } catch (err) {
      console.error('fetchFiles error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const updateEntry = useCallback((id: string, patch: Partial<UploadingFile>) => {
    setUploading(prev => prev.map(u => u.id === id ? { ...u, ...patch } : u));
  }, []);

  const removeEntry = useCallback((id: string, preview: string) => {
    setUploading(prev => prev.filter(u => u.id !== id));
    URL.revokeObjectURL(preview);
  }, []);

  const processSingleFile = useCallback(async (file: File, entry: UploadingFile) => {
    updateEntry(entry.id, { stage: 'compressing', progress: 15 });
    let blob: Blob;
    try {
      blob = await compressImage(file);
      updateEntry(entry.id, { progress: 45, compressedSize: blob.size });
    } catch {
      updateEntry(entry.id, { stage: 'error', error: 'فشل الضغط', progress: 0 });
      setTimeout(() => removeEntry(entry.id, entry.preview), 3000);
      return;
    }

    updateEntry(entry.id, { stage: 'uploading', progress: 65 });
    const bucket = BUCKET_MAP[entry.category];
    const folder = entry.category === 'products' ? 'products' : entry.category === 'hero' ? 'hero' : 'banners';
    const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.webp`;
    const path = `${folder}/${fileName}`;

    const { error } = await supabase.storage.from(bucket).upload(path, blob, {
      contentType: 'image/webp', cacheControl: '3600', upsert: false,
    });

    if (error) {
      updateEntry(entry.id, { stage: 'error', error: 'فشل الرفع', progress: 0 });
      setTimeout(() => removeEntry(entry.id, entry.preview), 3000);
      return;
    }

    const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(path);
    updateEntry(entry.id, { stage: 'done', progress: 100 });

    const newFile: MediaFile = {
      id: `${bucket}/${path}`,
      name: fileName,
      url: urlData.publicUrl,
      size: blob.size,
      created_at: new Date().toISOString(),
      bucket,
      path,
      category: entry.category,
    };

    setFiles(prev => [newFile, ...prev]);
    setTimeout(() => removeEntry(entry.id, entry.preview), 800);
    onToast('تم رفع الصورة بنجاح ✓');
  }, [updateEntry, removeEntry, onToast]);

  const processFiles = useCallback((rawFiles: File[]) => {
    const valid = rawFiles.filter(f => {
      if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(f.type)) return false;
      if (f.size > MAX_RAW_SIZE) { onToast(`${f.name} أكبر من 15 ميغابايت`, 'error'); return false; }
      return true;
    });
    if (!valid.length) return;

    const entries: UploadingFile[] = valid.map(f => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      name: f.name,
      progress: 0,
      stage: 'compressing' as const,
      preview: URL.createObjectURL(f),
      originalSize: f.size,
      category: uploadCategory,
    }));

    setUploading(prev => [...prev, ...entries]);
    entries.forEach((entry, i) => processSingleFile(valid[i], entry));
  }, [processSingleFile, uploadCategory, onToast]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    processFiles(Array.from(e.target.files ?? []));
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    processFiles(Array.from(e.dataTransfer.files));
  };

  const handleDelete = async (file: MediaFile) => {
    setDeleting(true);
    const { error } = await supabase.storage.from(file.bucket).remove([file.path]);
    if (error) {
      onToast('فشل حذف الصورة', 'error');
    } else {
      setFiles(prev => prev.filter(f => f.id !== file.id));
      setSelectedFiles(prev => { const s = new Set(prev); s.delete(file.id); return s; });
      if (previewFile?.id === file.id) setPreviewFile(null);
      onToast('تم حذف الصورة');
    }
    setDeleting(false);
    setDeleteConfirm(null);
  };

  const handleBulkDelete = async () => {
    const toDelete = files.filter(f => selectedFiles.has(f.id));
    let successCount = 0;
    for (const file of toDelete) {
      const { error } = await supabase.storage.from(file.bucket).remove([file.path]);
      if (!error) successCount++;
    }
    setFiles(prev => prev.filter(f => !selectedFiles.has(f.id)));
    setSelectedFiles(new Set());
    onToast(`تم حذف ${successCount} صورة`);
  };

  const copyUrl = (url: string, id: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopyTooltip(id);
      setTimeout(() => setCopyTooltip(null), 2000);
    });
  };

  const toggleSelect = (id: string) => {
    setSelectedFiles(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  };

  const selectAll = () => {
    if (selectedFiles.size === filteredFiles.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(filteredFiles.map(f => f.id)));
    }
  };

  // Filter & sort
  const filteredFiles = files
    .filter(f => {
      if (activeCategory !== 'all' && f.category !== activeCategory) return false;
      if (search && !f.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortMode === 'newest') return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      if (sortMode === 'oldest') return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      if (sortMode === 'name') return a.name.localeCompare(b.name);
      if (sortMode === 'size') return b.size - a.size;
      return 0;
    });

  const categoryCounts: Record<MediaCategory, number> = {
    all: files.length,
    products: files.filter(f => f.category === 'products').length,
    banners: files.filter(f => f.category === 'banners').length,
    hero: files.filter(f => f.category === 'hero').length,
    other: files.filter(f => f.category === 'other').length,
  };

  const totalSize = files.reduce((acc, f) => acc + f.size, 0);

  return (
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-bold text-[#1a1a1a] text-lg flex items-center gap-2">
            <i className="ri-image-2-line text-lg"></i>
            مكتبة الصور
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {files.length} صورة · {formatSize(totalSize)} مستخدم
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchFiles}
            className="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            title="تحديث"
          >
            <i className="ri-refresh-line text-sm"></i>
          </button>
          <button
            onClick={() => setViewMode(v => v === 'grid' ? 'list' : 'grid')}
            className="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            title={viewMode === 'grid' ? 'عرض قائمة' : 'عرض شبكة'}
          >
            <i className={`${viewMode === 'grid' ? 'ri-list-check-2' : 'ri-grid-line'} text-sm`}></i>
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {([
          { cat: 'products', label: 'منتجات', icon: 'ri-shopping-bag-3-line', color: 'text-emerald-600 bg-emerald-50' },
          { cat: 'banners', label: 'بانرات', icon: 'ri-layout-top-line', color: 'text-violet-600 bg-violet-50' },
          { cat: 'hero', label: 'هيرو', icon: 'ri-image-fill', color: 'text-amber-600 bg-amber-50' },
          { cat: 'other', label: 'أخرى', icon: 'ri-folder-image-line', color: 'text-gray-600 bg-gray-100' },
        ] as const).map(({ cat, label, icon, color }) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer text-right ${
              activeCategory === cat ? 'border-[#1a1a1a] bg-[#1a1a1a]/5' : 'border-gray-100 bg-white hover:border-gray-200'
            }`}
          >
            <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${color}`}>
              <i className={`${icon} text-sm`}></i>
            </div>
            <div>
              <p className="text-base font-bold text-[#1a1a1a]">{categoryCounts[cat]}</p>
              <p className="text-xs text-gray-400">{label}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Upload Zone */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#1a1a1a] flex items-center gap-2">
            <i className="ri-upload-cloud-2-line text-base"></i>
            رفع صور جديدة
          </h3>
          {/* Category selector for upload */}
          <div className="flex items-center gap-1 bg-gray-50 rounded-lg p-1">
            {(['products', 'banners', 'hero'] as const).map(cat => (
              <button
                key={cat}
                onClick={() => setUploadCategory(cat)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  uploadCategory === cat
                    ? 'bg-white text-[#1a1a1a] shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <i className={`${CATEGORY_ICONS[cat]} text-xs`}></i>
                {CATEGORY_LABELS[cat]}
              </button>
            ))}
          </div>
        </div>

        <div className="p-5">
          <div
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
              isDragging
                ? 'border-[#1a1a1a] bg-gray-50 scale-[1.01]'
                : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              onChange={handleFileInput}
              className="hidden"
            />
            <div className="flex flex-col items-center gap-3">
              <div className={`w-14 h-14 flex items-center justify-center rounded-2xl transition-all ${
                isDragging ? 'bg-[#1a1a1a] text-white' : 'bg-gray-100 text-gray-400'
              }`}>
                <i className="ri-upload-cloud-2-line text-2xl"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-gray-700">
                  {isDragging ? 'أفلت الصور هنا' : 'اسحب وأفلت الصور هنا'}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  أو <span className="text-[#1a1a1a] font-semibold underline">انقر للاختيار</span> من جهازك
                </p>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <i className="ri-file-image-line"></i>
                  PNG · JPG · WEBP · GIF
                </span>
                <span>·</span>
                <span>حتى 15 ميغابايت</span>
                <span>·</span>
                <span className="flex items-center gap-1 text-emerald-600">
                  <i className="ri-magic-line"></i>
                  ضغط WebP تلقائي
                </span>
              </div>
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium ${CATEGORY_COLORS[uploadCategory]}`}>
                <i className={CATEGORY_ICONS[uploadCategory]}></i>
                سيُرفع في: {CATEGORY_LABELS[uploadCategory]}
              </div>
            </div>
          </div>

          {/* Upload Progress */}
          {uploading.length > 0 && (
            <div className="mt-4 space-y-2">
              {uploading.map(u => (
                <div
                  key={u.id}
                  className={`flex items-center gap-3 rounded-xl p-3 border transition-all ${
                    u.stage === 'error' ? 'border-red-100 bg-red-50'
                    : u.stage === 'done' ? 'border-emerald-100 bg-emerald-50'
                    : 'border-gray-100 bg-gray-50'
                  }`}
                >
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                    <img src={u.preview} alt={u.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <p className="text-xs font-medium text-gray-700 truncate">{u.name}</p>
                      {u.compressedSize && !u.error && (
                        <span className="text-[10px] text-emerald-600 font-bold flex-shrink-0 whitespace-nowrap">
                          {formatSize(u.originalSize)} → {formatSize(u.compressedSize)}
                        </span>
                      )}
                    </div>
                    {u.stage === 'error' ? (
                      <p className="text-xs text-red-500">{u.error}</p>
                    ) : (
                      <>
                        <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                          <div
                            className={`h-1.5 rounded-full transition-all duration-500 ${
                              u.stage === 'done' ? 'bg-emerald-500'
                              : u.stage === 'compressing' ? 'bg-amber-400'
                              : 'bg-[#1a1a1a]'
                            }`}
                            style={{ width: `${u.progress}%` }}
                          />
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1">
                          {u.stage === 'compressing' ? 'جاري الضغط...'
                          : u.stage === 'done' ? 'تم بنجاح!'
                          : 'جاري الرفع...'}
                        </p>
                      </>
                    )}
                  </div>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    {u.stage === 'done' ? <i className="ri-check-line text-emerald-500 text-sm"></i>
                    : u.stage === 'error' ? <i className="ri-error-warning-line text-red-400 text-sm"></i>
                    : <i className="ri-loader-4-line animate-spin text-gray-400 text-sm"></i>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Library */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {/* Toolbar */}
        <div className="px-5 py-4 border-b border-gray-50 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-1 overflow-x-auto flex-shrink-0">
            {(['all', 'products', 'banners', 'hero', 'other'] as MediaCategory[]).map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-[#1a1a1a] text-white'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                <i className={`${CATEGORY_ICONS[cat]} text-xs`}></i>
                {CATEGORY_LABELS[cat]}
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  activeCategory === cat ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
                }`}>
                  {categoryCounts[cat]}
                </span>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 flex-1 w-full sm:w-auto">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute top-1/2 -translate-y-1/2 right-3 text-gray-300 text-sm"></i>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="بحث في الصور..."
                className="w-full border border-gray-200 rounded-lg pr-8 pl-3 py-2 text-xs outline-none focus:border-[#1a1a1a] transition-colors"
              />
            </div>
            <select
              value={sortMode}
              onChange={e => setSortMode(e.target.value as SortMode)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-[#1a1a1a] transition-colors cursor-pointer bg-white"
            >
              <option value="newest">الأحدث</option>
              <option value="oldest">الأقدم</option>
              <option value="name">الاسم</option>
              <option value="size">الحجم</option>
            </select>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedFiles.size > 0 && (
          <div className="px-5 py-3 bg-[#1a1a1a]/5 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-[#1a1a1a]">
                {selectedFiles.size} صورة محددة
              </span>
              <button
                onClick={selectAll}
                className="text-xs text-gray-500 hover:text-gray-700 underline cursor-pointer"
              >
                {selectedFiles.size === filteredFiles.length ? 'إلغاء التحديد' : 'تحديد الكل'}
              </button>
            </div>
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-bold px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-delete-bin-line text-sm"></i>
              حذف المحدد
            </button>
          </div>
        )}

        {/* Content */}
        <div className="p-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="w-10 h-10 border-2 border-gray-200 border-t-[#1a1a1a] rounded-full animate-spin"></div>
              <p className="text-sm text-gray-400">جاري تحميل الصور...</p>
            </div>
          ) : filteredFiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-2xl">
                <i className="ri-image-2-line text-gray-300 text-3xl"></i>
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-500">لا توجد صور</p>
                <p className="text-xs text-gray-400 mt-1">
                  {search ? 'لا توجد نتائج للبحث' : 'ارفع صورك الأولى من الأعلى'}
                </p>
              </div>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {filteredFiles.map(file => (
                <div
                  key={file.id}
                  className={`group relative rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                    selectedFiles.has(file.id)
                      ? 'border-[#1a1a1a]'
                      : 'border-transparent hover:border-gray-200'
                  }`}
                  onClick={() => setPreviewFile(file)}
                >
                  <div className="aspect-square bg-gray-100">
                    <img
                      src={file.url}
                      alt={file.name}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <button
                      onClick={e => { e.stopPropagation(); copyUrl(file.url, file.id); }}
                      className="w-8 h-8 flex items-center justify-center bg-white rounded-lg text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
                      title="نسخ الرابط"
                    >
                      {copyTooltip === file.id
                        ? <i className="ri-check-line text-emerald-500 text-sm"></i>
                        : <i className="ri-links-line text-sm"></i>}
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); setDeleteConfirm(file); }}
                      className="w-8 h-8 flex items-center justify-center bg-white rounded-lg text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                      title="حذف"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>

                  {/* Select checkbox */}
                  <div
                    className={`absolute top-2 right-2 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all cursor-pointer ${
                      selectedFiles.has(file.id)
                        ? 'bg-[#1a1a1a] border-[#1a1a1a]'
                        : 'bg-white/80 border-gray-300 opacity-0 group-hover:opacity-100'
                    }`}
                    onClick={e => { e.stopPropagation(); toggleSelect(file.id); }}
                  >
                    {selectedFiles.has(file.id) && <i className="ri-check-line text-white text-[10px]"></i>}
                  </div>

                  {/* Category badge */}
                  <div className="absolute top-2 left-2">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${CATEGORY_COLORS[file.category]}`}>
                      {CATEGORY_LABELS[file.category]}
                    </span>
                  </div>

                  {/* File info */}
                  <div className="p-2 bg-white">
                    <p className="text-[10px] text-gray-500 truncate">{file.name}</p>
                    <p className="text-[9px] text-gray-300">{formatSize(file.size)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredFiles.map(file => (
                <div
                  key={file.id}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
                    selectedFiles.has(file.id)
                      ? 'border-[#1a1a1a] bg-[#1a1a1a]/5'
                      : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                  }`}
                  onClick={() => setPreviewFile(file)}
                >
                  <div
                    className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
                      selectedFiles.has(file.id)
                        ? 'bg-[#1a1a1a] border-[#1a1a1a]'
                        : 'border-gray-300'
                    }`}
                    onClick={e => { e.stopPropagation(); toggleSelect(file.id); }}
                  >
                    {selectedFiles.has(file.id) && <i className="ri-check-line text-white text-[10px]"></i>}
                  </div>

                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                    <img src={file.url} alt={file.name} className="w-full h-full object-cover object-top" loading="lazy" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${CATEGORY_COLORS[file.category]}`}>
                        {CATEGORY_LABELS[file.category]}
                      </span>
                      <span className="text-[10px] text-gray-400">{formatSize(file.size)}</span>
                      <span className="text-[10px] text-gray-400">{formatDate(file.created_at)}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={e => { e.stopPropagation(); copyUrl(file.url, file.id); }}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
                      title="نسخ الرابط"
                    >
                      {copyTooltip === file.id
                        ? <i className="ri-check-line text-emerald-500 text-sm"></i>
                        : <i className="ri-links-line text-sm"></i>}
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); setDeleteConfirm(file); }}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                      title="حذف"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {filteredFiles.length > 0 && (
          <div className="px-5 py-3 border-t border-gray-50 flex items-center justify-between">
            <p className="text-xs text-gray-400">{filteredFiles.length} صورة</p>
            <button
              onClick={selectAll}
              className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer"
            >
              {selectedFiles.size === filteredFiles.length && filteredFiles.length > 0
                ? 'إلغاء تحديد الكل'
                : 'تحديد الكل'}
            </button>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewFile && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
          onClick={() => setPreviewFile(null)}
          dir="rtl"
        >
          <div
            className="bg-white rounded-2xl overflow-hidden w-full max-w-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Image */}
            <div className="relative bg-gray-100 aspect-video flex items-center justify-center">
              <img
                src={previewFile.url}
                alt={previewFile.name}
                className="max-w-full max-h-full object-contain"
              />
              <button
                onClick={() => setPreviewFile(null)}
                className="absolute top-3 left-3 w-8 h-8 flex items-center justify-center bg-black/50 hover:bg-black/70 text-white rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
              <div className="absolute top-3 right-3">
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${CATEGORY_COLORS[previewFile.category]}`}>
                  {CATEGORY_LABELS[previewFile.category]}
                </span>
              </div>
            </div>

            {/* Info */}
            <div className="p-5">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-[#1a1a1a] truncate">{previewFile.name}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-gray-400">{formatSize(previewFile.size)}</span>
                    <span className="text-xs text-gray-400">{formatDate(previewFile.created_at)}</span>
                  </div>
                </div>
              </div>

              {/* URL */}
              <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-3 mb-4">
                <p className="flex-1 text-xs text-gray-500 truncate font-mono" dir="ltr">{previewFile.url}</p>
                <button
                  onClick={() => copyUrl(previewFile.url, previewFile.id)}
                  className="flex items-center gap-1.5 bg-[#1a1a1a] text-white text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-[#333] transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
                >
                  {copyTooltip === previewFile.id
                    ? <><i className="ri-check-line"></i> تم النسخ</>
                    : <><i className="ri-links-line"></i> نسخ الرابط</>}
                </button>
              </div>

              <div className="flex gap-2">
                <a
                  href={previewFile.url}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-1.5 border border-gray-200 text-gray-700 text-sm font-medium py-2.5 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-download-line text-sm"></i>
                  تحميل
                </a>
                <button
                  onClick={() => { setDeleteConfirm(previewFile); setPreviewFile(null); }}
                  className="flex items-center justify-center gap-1.5 bg-red-50 border border-red-200 text-red-600 text-sm font-medium px-4 py-2.5 rounded-xl hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                  حذف
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" dir="rtl">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-xl mb-4 mx-auto">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="font-bold text-[#1a1a1a] text-center mb-2">حذف الصورة</h3>
            <p className="text-sm text-gray-500 text-center mb-1">
              هل أنت متأكد من حذف هذه الصورة؟
            </p>
            <p className="text-xs text-gray-400 text-center mb-6 truncate px-4">{deleteConfirm.name}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 border border-gray-200 rounded-xl py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                إلغاء
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={deleting}
                className="flex-1 bg-red-500 text-white rounded-xl py-2.5 text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? <i className="ri-loader-4-line animate-spin"></i> : null}
                حذف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
